/**
 * Labstep
 *
 * @module components/Filter/Active
 * @desc Active Filters
 */

import { DeviceFilterActive } from 'labstep-web/components/Device/Filter/Active';
import { ExperimentWorkflowFilterProtocolActive } from 'labstep-web/components/ExperimentWorkflow/Filter/Protocol/Active';
import { ExperimentWorkflowFilterResourceActive } from 'labstep-web/components/ExperimentWorkflow/Filter/Resource/Active';
import { ExperimentWorkflowFilterSignedActive } from 'labstep-web/components/ExperimentWorkflow/Filter/Signed/Active';
import { ExperimentWorkflowFilterStatusActive } from 'labstep-web/components/ExperimentWorkflow/Filter/Status/Active';
import { ExperimentWorkflowFilterStatusTypeActive } from 'labstep-web/components/ExperimentWorkflow/Filter/StatusType/Active';
import { FileFilterExtensionActive } from 'labstep-web/components/File/Filter/Extension/Active';
import { FilterDeletedActive } from 'labstep-web/components/Filter/Deleted/Active';
import { FilterLogCreatedByActive } from 'labstep-web/components/Filter/LogCreatedBy/Active';
import { FilterResourceItemStatusActive } from 'labstep-web/components/Filter/ResourceItemStatus/Active';
import { FilterSearchActive } from 'labstep-web/components/Filter/Search/Active';
import FilterTagActive from 'labstep-web/components/Filter/Tag/Active';
import { FilterUserActive } from 'labstep-web/components/Filter/User/Active';
import FilterPostActive from 'labstep-web/components/FilterPost/Active';
import { LogFilterGroupActive } from 'labstep-web/components/Log/Filter/Group/Active';
import { LogFilterObjectClassActive } from 'labstep-web/components/Log/Filter/ObjectClass/Active';
import { MoleculeFilterActive } from 'labstep-web/components/Molecule/Filter/Active';
import { ProtocolCollectionFilterStatusActive } from 'labstep-web/components/ProtocolCollection/Filter/Status/Active';
import { ResourceLocationFilterActive } from 'labstep-web/components/ResourceLocation/Filter/Active';
import { TagFilterTypeActive } from 'labstep-web/components/Tag/Filter/Type/Active';
import { RemoveLabel } from 'labstep-web/core/Label/Remove';
import { dateOnly } from 'labstep-web/services/date.service';
import type React from 'react';
import styles from './styles.module.scss';
import type {
  DateRangeFilterActiveProps,
  IFilterActiveProps,
} from './types';
import { getHasFilterActive } from './utils';

export const DateRangeFilterActive: React.FC<
  DateRangeFilterActiveProps
> = ({ dateFrom, dateTo, clearParams }) => {
  if (!dateTo || !dateFrom) {
    return null;
  }

  const toText = dateOnly(dateTo);
  const fromText = dateOnly(dateFrom);

  return (
    <RemoveLabel
      name={`${fromText} - ${toText}`}
      icon="calendar alternate"
      onRemoveClick={() =>
        clearParams &&
        clearParams(['created_at_from', 'created_at_to'])
      }
    />
  );
};

export const FilterActive: React.FC<IFilterActiveProps> = ({
  searchParams,
  setParams,
  clearParams,
  showSearchQuery,
  setPostFilterNodeType,
  removePostFilter,
  clearAll,
  entityView,
}) => {
  if (!searchParams || !clearParams || !setParams) {
    return null;
  }
  const hasParams = getHasFilterActive(searchParams, showSearchQuery);
  if (!hasParams) {
    return null;
  }
  return (
    <div data-testid="filter-active" className={styles.container}>
      <span>Filtering by:</span>
      {setPostFilterNodeType &&
        removePostFilter &&
        clearAll &&
        entityView && (
          <FilterPostActive searchParams={searchParams} />
        )}
      <DateRangeFilterActive
        clearParams={clearParams}
        dateFrom={searchParams.created_at_from as string}
        dateTo={searchParams.created_at_to as string}
      />
      <FilterUserActive
        searchParams={searchParams}
        clearParams={clearParams}
        type="author"
      />
      <FilterTagActive
        setParams={setParams}
        searchParams={searchParams}
      />
      <TagFilterTypeActive
        clearParams={clearParams}
        searchParams={searchParams}
      />
      <FilterResourceItemStatusActive
        clearParams={clearParams}
        searchParams={searchParams}
      />
      <ExperimentWorkflowFilterStatusTypeActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <ProtocolCollectionFilterStatusActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <ExperimentWorkflowFilterStatusActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <FileFilterExtensionActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <ExperimentWorkflowFilterSignedActive
        clearParams={clearParams}
        searchParams={searchParams}
      />
      <ExperimentWorkflowFilterProtocolActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <ExperimentWorkflowFilterResourceActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <DeviceFilterActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <FilterDeletedActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <FilterUserActive
        searchParams={searchParams}
        clearParams={clearParams}
        type="assignee"
      />
      <ResourceLocationFilterActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <MoleculeFilterActive
        entityName={entityView?.entity_name || ''}
        searchParams={searchParams}
        clearParams={clearParams}
        setParams={setParams}
      />
      <LogFilterGroupActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <LogFilterObjectClassActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      <FilterLogCreatedByActive
        searchParams={searchParams}
        clearParams={clearParams}
      />
      {showSearchQuery && (
        <FilterSearchActive
          searchParams={searchParams}
          clearParams={clearParams}
        />
      )}
    </div>
  );
};
